"use client";

import { useId } from "react";

/**
 * The circular backdrop behind lead form 9's terminal screens.
 *
 * A 460px circle anchored ABOVE the card top, so only its bottom arc shows once the card
 * clips it (`overflow-hidden` on the card). The fill is a vertical gradient built from a
 * single configurable colour — lighter where it meets the card top, deeper toward the arc.
 *
 * ─── Why this lives in its own file ─────────────────────────────────────────────────
 * Both the hand-built widget (`Leaddrop9`) and the config-driven skin 3000 draw this exact
 * shape. A second copy would drift, and the whole point of skin 3000 is that a visitor who
 * meets both forms on one site cannot tell them apart. Importing `Leaddrop9.tsx` from the
 * skin would have worked too, but that module is ~1300 lines of widget wired to Redux and
 * the lead APIs — a heavy import to pay for one SVG.
 *
 * The gradient id is per-instance (`useId`). A fixed id breaks the moment two of these
 * render on one page with different colours: SVG resolves `url(#id)` against the FIRST
 * matching def, so the second card would silently borrow the first card's colour.
 */
export function Leaddrop9CircleBg({ color }: { color: string }) {
  const gradientId = `leaddrop9-circle-grad-${useId()}`;
  return (
    <svg
      viewBox="0 0 460 460"
      preserveAspectRatio="xMidYMid meet"
      aria-hidden
      className="pointer-events-none absolute left-1/2 top-[-260px] size-[460px] -translate-x-1/2"
    >
      <defs>
        <linearGradient id={gradientId} x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor={color} stopOpacity="0.45" />
          <stop offset="100%" stopColor={color} stopOpacity="1" />
        </linearGradient>
      </defs>
      <circle cx="230" cy="230" r="230" fill={`url(#${gradientId})`} />
    </svg>
  );
}

export default Leaddrop9CircleBg;
